import { createContext, useState, useEffect, useContext, useRef } from "react";
import { useLocation, useSearchParams } from "react-router-dom";

import { getVerifiedToken, getIndividuals } from "./util";
import { getBanks, submitHRASetup, submitInsurance } from "./util";

import { UserContext } from "../../../../context/UserContext";
import { get_user_from_token } from "../../../../api/userService";
import { get } from "lodash";
import { toast } from "react-toastify";

export const Context = createContext({});

export const ContextProvider = ({ children }) => {
  let { user, setUser, refreshUserProfile, getUserPlan } =
    useContext(UserContext);

  const [affordability, setAffordability] = useState({
    affordable: null,
    optOut: null,
  });

  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  const [ready, setReady] = useState(() => false);
  const [hraSetup, setHraSetup] = useState(() => null);
  const [individuals, setIndividuals] = useState(() => null);
  const [providers, setProviders] = useState(() => null);
  const [showDependentForm, setShowDependentForm] = useState(false);

  const [roadmapIndex, setRoadmapIndex] = useState(() => 0);
  const [addDependent, setAddDependent] = useState(() => false);

  // Step index and names for hra setup process.  Always starts at 0 and "Acknowledge"
  const mode = location?.pathname?.split("/")?.[2];
  let userPlanId = location?.pathname?.split("/")?.[3];
  const inputFormName = location?.pathname?.split("/")?.[4];

  const token = searchParams.get("token");
  const form = searchParams.get("form");
  if (!userPlanId) {
    userPlanId = searchParams.get("planId");
  }

  const [banks, setBanks] = useState(null);

  // Track if token has been processed
  const processedTokenRef = useRef(null);
  const isProcessingRef = useRef(false);

  useEffect(() => {
    if (
      //			!user &&
      mode === "add" &&
      userPlanId &&
      token &&
      processedTokenRef.current !== token &&
      !isProcessingRef.current
    ) {
      processedTokenRef.current = token;
      isProcessingRef.current = true;
      const getUserFromToken = async (token) => {
        try {
          const verifiedToken = await getVerifiedToken(token);
          if (verifiedToken) {
            localStorage.setItem("authenticatedToken", verifiedToken);
            localStorage.setItem("token", verifiedToken);
            const result = await get_user_from_token(verifiedToken);
            const userFromToken = result?.data;
            if (userFromToken?.userId?.length > 0) {
              await getAllBanks();
              handleData(userFromToken);
              setUser(userFromToken);
            }
          }
        } catch (error) {
          console.error("Error in getUserFromToken: ", error);
          alert("Invalid or expired token. Please contact support.");
        }
      };
      getUserFromToken(token).finally(() => {
        isProcessingRef.current = false;
      });
    }
  }, [user, mode, userPlanId, token]); // Keep dependencies but guard with refs

  const getAllBanks = async () => {
    try {
      if (!banks || banks?.size === 0) {
        const banksData = await getBanks();
        setBanks(banksData);
        return banksData;
      }
      return banks;
    } catch (error) {
      console.error("Error fetching banks: ", error);
      return [];
    }
  };

  async function getData() {
    try {
      await getAllBanks();
      const data = handleData(user);
      return data;
    } catch (error) {
      console.error(error);
    }
  }

  function handleData(profile) {
    const dependents = get(profile, "dependents", []);
    const individuals = getIndividuals(profile, dependents);
    const providers = profile?.providers;
    setHraSetup(profile);
    setIndividuals(individuals);
    setProviders(providers);

    setReady(true);

    const result = {
      individuals,
      providers,
    };
    return result;
  }

  function handleParams(action, key, value = "") {
    const params = new URLSearchParams(searchParams);

    if (action === "set") params.set(key, value);
    else if (action === "delete") params.delete(key);

    setSearchParams(params);
  }

  async function handleSubmit(section, values, formik) {
    const userPlan = getUserPlan(userPlanId);

    try {
      switch (section) {
        case "hraSetup": {
          await submitHRASetup(values, userPlan);
          await refreshUserProfile();
          break;
        }
        case "insurance": {
          try {
            await submitInsurance(values, user, userPlan);
          } catch (error) {
            console.error(error);
            toast.error(
              error?.response?.data?.message ||
                "We couldn't save your insurance. Please try again.",
            );
            return;
          }
          await refreshUserProfile();
          break;
        }
        case "reimbursement": {
          break;
        }
        case "notification": {
          break;
        }
      }
    } catch (error) {
      console.error(error);
    }
  }

  const [showOTP, setShowOTP] = useState(false);
  const [showOTPOption, setShowOTPOption] = useState(false);
  const [medium, setMedium] = useState("email");
  const [code, setCode] = useState("");

  return (
    <Context.Provider
      value={{
        ready,
        showOTP,
        setShowOTP,
        showOTPOption,
        setShowOTPOption,
        medium,
        setMedium,
        code,
        setCode,
        mode,
        form,
        inputFormName,
        getData,
        handleParams,
        handleSubmit,
        affordability,
        setAffordability,
        addDependent,
        setAddDependent,
        showDependentForm,
        setShowDependentForm,
        user,
        userPlanId,
        hraSetup,
        individuals,
        providers,
        banks,

        roadmapIndex,
        setRoadmapIndex,
      }}
    >
      {children}
    </Context.Provider>
  );
};
